import type { ChildApp } from "./appList";
import type { AppHost } from "./contract";

// Every child app lives under /apps/<slug>; the child sees only the part after that.
export const CHILD_APP_BASE = "/apps";

export const childAppPath = (slug: string) => `${CHILD_APP_BASE}/${slug}`;

// The slug of an /apps/<slug>[/...] path, or null for any other path.
export function slugFromPath(pathname: string): string | null {
  const match = new RegExp(`^${CHILD_APP_BASE}/([a-z0-9][a-z0-9-]*)(?:/|$)`).exec(pathname);
  return match ? match[1] : null;
}

// The route the child sees: AMP's path with /apps/<slug> taken off ("/" at the app root).
export function childRoute(pathname: string, slug: string): string {
  const prefix = childAppPath(slug);
  if (pathname === prefix) return "/";
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return "/";
}

// The AMP path for a route the child asked to go to.
export function appPathFor(slug: string, to: string): string {
  const relative = to.startsWith("/") ? to : `/${to}`;
  return relative === "/" ? childAppPath(slug) : `${childAppPath(slug)}${relative}`;
}

export interface ChildHostOptions {
  app: ChildApp;
  user: AppHost["user"];
  initialRoute: string;
  navigate: (path: string, opts?: { replace?: boolean }) => void;
  getToken: (scopes?: string[]) => Promise<string>;
  telemetry: AppHost["telemetry"];
  apimSubscriptionKey?: string;
  product?: string;
}

// The AppHost a child gets at mount(). AMP has no dark mode, so the theme is
// always light. notifyRoute is called by AMP whenever the browser route moves,
// so the child's subscribeRoute listeners see back/forward and link clicks.
export function createChildHost(options: ChildHostOptions): {
  host: AppHost;
  notifyRoute: (route: string) => void;
} {
  const { app } = options;
  const listeners = new Set<(route: string) => void>();
  let route = options.initialRoute;

  const host: AppHost = {
    contractVersion: 1,
    apiBaseUrl: app.apiBaseUrl,
    apimSubscriptionKey: options.apimSubscriptionKey,
    product: options.product,
    getAuthToken: options.getToken,
    user: options.user,
    get theme() {
      return "light" as const;
    },
    subscribeTheme: (callback) => {
      callback("light");
      return () => {};
    },
    get route() {
      return route;
    },
    navigate: (to, opts) => options.navigate(appPathFor(app.slug, to), opts),
    subscribeRoute: (callback) => {
      listeners.add(callback);
      return () => {
        listeners.delete(callback);
      };
    },
    uiElements: app.uiElements,
    telemetry: options.telemetry,
  };

  return {
    host,
    notifyRoute: (next) => {
      if (next === route) return;
      route = next;
      listeners.forEach((callback) => callback(next));
    },
  };
}
